"use client";

import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { MessageSquare } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

type AgentType = "voice_agent" | "email_agent";

interface QAFeedbackLinkButtonProps {
  agentId: string;
  agentType: AgentType;
  agentName?: string;
  className?: string;
  /** Only count pending (not in_progress) - default true */
  pendingOnly?: boolean;
}

export function QAFeedbackLinkButton({
  agentId,
  agentType,
  agentName,
  className,
  pendingOnly = true,
}: QAFeedbackLinkButtonProps) {
  const { fetchWithAuth } = useAuth();
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadCount = useCallback(async () => {
    if (!agentId) return;

    try {
      setIsLoading(true);

      if (pendingOnly) {
        // Only pending feedback
        const res = await fetchWithAuth(
          `/api/ai/feedback?agent_type=${agentType}&status=pending&agent_id=${agentId}&limit=1`
        );
        if (res.ok) {
          const data = await res.json();
          setCount(data.total || 0);
        }
      } else {
        // Pending + in_progress
        const [pendingRes, inProgressRes] = await Promise.all([
          fetchWithAuth(
            `/api/ai/feedback?agent_type=${agentType}&status=pending&agent_id=${agentId}&limit=1`
          ),
          fetchWithAuth(
            `/api/ai/feedback?agent_type=${agentType}&status=in_progress&agent_id=${agentId}&limit=1`
          ),
        ]);

        if (pendingRes.ok && inProgressRes.ok) {
          const pendingData = await pendingRes.json();
          const inProgressData = await inProgressRes.json();
          setCount((pendingData.total || 0) + (inProgressData.total || 0));
        }
      }
    } catch (err) {
      console.error("Failed to load feedback count:", err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, agentId, agentType, pendingOnly]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  // Build URL based on agent type
  const href =
    agentType === "voice_agent"
      ? `/?page=qa-feedback&voiceAgentId=${agentId}${agentName ? `&agentName=${encodeURIComponent(agentName)}` : ""}`
      : `/?page=qa-feedback&agentId=${agentId}${agentName ? `&agentName=${encodeURIComponent(agentName)}` : ""}`;

  return (
    <a
      href={href}
      className={cn(
        "inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-md border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors",
        className
      )}
    >
      <MessageSquare className="w-4 h-4" />
      <span>QA Feedback</span>
      {!isLoading && count > 0 && (
        <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] justify-center">
          {count}
        </Badge>
      )}
    </a>
  );
}
